import * as Sentry from '@sentry/react';
import qs from 'query-string';
import getValidatedParams from './getValidatedParams';
import { safeString } from './schemaUtils';

const COMBEE_URL = process.env.COMBEE_URL || '';

export const OIDC_PARAM = 'uid';

export function getOidcId(search: string): string | undefined {
  const { [OIDC_PARAM]: oidcId } = getValidatedParams({
    params: qs.parse(search),
    schema: { [OIDC_PARAM]: safeString },
  });

  return oidcId;
}

export type OidcInteractionAction = 'login' | 'confirm' | 'abort';

export async function oidcPostInteraction(
  uid: string,
  action: OidcInteractionAction,
  options: { jwt?: string; body?: unknown } = {},
) {
  const { jwt, body } = options;
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (jwt) headers.Authorization = `Bearer ${jwt}`;

  try {
    const response = await fetch(`${COMBEE_URL}/interaction/${uid}/${action}`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      throw new Error(`OIDC interaction ${action} failed: ${response.status}`);
    }
    const data = (await response.json()) as { redirectTo?: string };
    if (!data.redirectTo) {
      throw new Error(`OIDC interaction ${action} response missing redirectTo`);
    }
    globalThis.location.href = data.redirectTo;
  } catch (error) {
    Sentry.captureException(error, {
      tags: { feature: 'oidc-interaction', action },
      extra: { uid },
    });
    throw error;
  }
}

export type ClientData = {
  prompt: {
    details: {
      missingOIDCScope?: string[];
    };
  };
  client: {
    name: string;
    logo: string | null;
  };
};

export async function oidcGetInteraction(uid: string, jwt?: string): Promise<ClientData> {
  const headers: Record<string, string> = {};
  if (jwt) headers.Authorization = `Bearer ${jwt}`;

  try {
    const response = await fetch(`${COMBEE_URL}/interaction/${uid}`, {
      method: 'GET',
      credentials: 'include',
      headers,
    });
    if (!response.ok) {
      throw new Error(`OIDC interaction GET failed: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    Sentry.captureException(error, {
      tags: { feature: 'oidc-interaction', action: 'get' },
      extra: { uid },
    });
    throw error;
  }
}
